import React, { useState, useEffect, useCallback } from 'react';
import { Formik } from 'formik';
import * as Yup from 'yup';
import {
  Container,
  Header,
  HeaderLogo,
  Content,
  SubHeader,
  AnalysisTitle,
  FormSection,
  FooterButtons,
  BackButton,
  SubmitButton,
  LoadingContainer,
  LoadingText,
  ProgressTrack,
  ProgressBar,
  ResultCard,
  CardTopRow,
  InsurerLogo,
  InsurerInfo,
  InsurerName,
  DetailsRow,
  DetailColumn,
  DetailLabel,
  DetailValue,
  ActionButtonsRow,
  FinalBackButton,
  ValidationContainer,
  ValidateTitle,
  ValidationSectionTitle,
  InputGroup,
  StyledInput,
  StatusBadge,
  StatusDot,
  StatusText,
  Divider,
  RadioGroup,
  RadioButton,
  CustomRadio
} from '../styles';
import { FaChevronLeft } from 'react-icons/fa';
import logorv from '../../../resources/imgs/logo-lado-bom-seguros.svg';
import Input from '../../../components/Input';
import { FieldLabel } from '../../../styles';
import { Dropdown } from 'semantic-ui-react';
import { warningNotification, successNotification } from '../../../services/notification';
import { get, post } from '../../../services/request';
import moment from 'moment-timezone';
import { numberToMoneyString } from '../../../services/masks';

interface YelumBudgetFormProps {
  estate: any;
  insurerId: string;
  userId: string;
  percentage: number;
  onBudgetCreated?: (budgetId: string) => void;
  onBack: () => void;
}

type AnalysisStep = 'LOADING' | 'FORM' | 'RESULT';

type CoverageControl = {
  id: string;
  code: string;
  value: number;
  onEditValue: number;
  name: string;
  checked: boolean;
  onEdit: boolean;
  multiplier?: number;
  defaultMultiplier?: number;
  minValue?: number;
  maxValue?: number;
  defaultValue?: number;
  defaultEnabled?: boolean;
  isRequired?: boolean;
  coverage?: any;
  propertyType?: string;
};

const YELUM_PROPERTY_LIMITS: Record<string, Record<string, number>> = {
  Apartamento: {
    INCENDIO_EXPLOSAO_DE_QUALQUER_NATUREZA_FUMACA_E_QUEDA_DE_AERONAVES_PREDIO_E_CONTEUDO:
      2500000,
    INCENDIO_EXPLOSAO_DE_QUALQUER_NATUREZA_FUMACA_E_QUEDA_DE_AERONAVES_CONTEUDO: 2500000,
    INCENDIO_EXPLOSAO_DE_QUALQUER_NATUREZA_FUMACA_E_QUEDA_DE_AERONAVES_PREDIO: 2500000,
    DANOS_ELETRICOS_VERBA_UNICA: 125000,
    ROUBO_E_OU_SUBTRACAO_DE_BENS_COM_VESTIGIOS_VERBA_UNICA: 150000,
    VENDAVAL_FURACAO_CICLONE_TORNADO_E_GRANIZO_VERBA_UNICA: 250000,
    PERDA_OU_PAGAMENTO_DE_ALUGUEL_VERBA_UNICA: 200000,
    RESPONSABILIDADE_CIVIL_FAMILIAR_VERBA_UNICA: 500000,
    IMPACTO_DE_VEICULOS_TERRESTRES_VERBA_UNICA: 500000,
    QUEBRA_DE_VIDROS_ESPELHOS_MARMORES_E_GRANITOS_VERBA_UNICA: 100000,
    ROMPIMENTO_DE_TUBULACOES_DE_AGUA_VERBA_UNICA: 50000
  },
  'Residência Alvenaria': {
    INCENDIO_EXPLOSAO_DE_QUALQUER_NATUREZA_FUMACA_E_QUEDA_DE_AERONAVES_PREDIO_E_CONTEUDO:
      2500000,
    INCENDIO_EXPLOSAO_DE_QUALQUER_NATUREZA_FUMACA_E_QUEDA_DE_AERONAVES_CONTEUDO: 2500000,
    INCENDIO_EXPLOSAO_DE_QUALQUER_NATUREZA_FUMACA_E_QUEDA_DE_AERONAVES_PREDIO: 2500000,
    DANOS_ELETRICOS_VERBA_UNICA: 125000,
    ROUBO_E_OU_SUBTRACAO_DE_BENS_COM_VESTIGIOS_VERBA_UNICA: 150000,
    VENDAVAL_FURACAO_CICLONE_TORNADO_E_GRANIZO_VERBA_UNICA: 250000,
    PERDA_OU_PAGAMENTO_DE_ALUGUEL_VERBA_UNICA: 200000,
    RESPONSABILIDADE_CIVIL_FAMILIAR_VERBA_UNICA: 500000,
    IMPACTO_DE_VEICULOS_TERRESTRES_VERBA_UNICA: 500000,
    QUEBRA_DE_VIDROS_ESPELHOS_MARMORES_E_GRANITOS_VERBA_UNICA: 100000,
    ROMPIMENTO_DE_TUBULACOES_DE_AGUA_VERBA_UNICA: 50000
  },
  'Casa Madeira': {
    INCENDIO_EXPLOSAO_DE_QUALQUER_NATUREZA_FUMACA_E_QUEDA_DE_AERONAVES_PREDIO_E_CONTEUDO:
      375000,
    INCENDIO_EXPLOSAO_DE_QUALQUER_NATUREZA_FUMACA_E_QUEDA_DE_AERONAVES_CONTEUDO: 375000,
    INCENDIO_EXPLOSAO_DE_QUALQUER_NATUREZA_FUMACA_E_QUEDA_DE_AERONAVES_PREDIO: 375000,
    DANOS_ELETRICOS_VERBA_UNICA: 30000,
    ROUBO_E_OU_SUBTRACAO_DE_BENS_COM_VESTIGIOS_VERBA_UNICA: 30000,
    VENDAVAL_FURACAO_CICLONE_TORNADO_E_GRANIZO_VERBA_UNICA: 30000,
    PERDA_OU_PAGAMENTO_DE_ALUGUEL_VERBA_UNICA: 30000,
    RESPONSABILIDADE_CIVIL_FAMILIAR_VERBA_UNICA: 30000,
    IMPACTO_DE_VEICULOS_TERRESTRES_VERBA_UNICA: 30000,
    QUEBRA_DE_VIDROS_ESPELHOS_MARMORES_E_GRANITOS_VERBA_UNICA: 20000,
    ROMPIMENTO_DE_TUBULACOES_DE_AGUA_VERBA_UNICA: 50000.01
  },
  'Escritório e Consultório': {
    INCENDIO_EXPLOSAO_DE_QUALQUER_NATUREZA_FUMACA_E_QUEDA_DE_AERONAVES_PREDIO_E_CONTEUDO:
      2500000,
    INCENDIO_EXPLOSAO_DE_QUALQUER_NATUREZA_FUMACA_E_QUEDA_DE_AERONAVES_CONTEUDO: 2500000,
    INCENDIO_EXPLOSAO_DE_QUALQUER_NATUREZA_FUMACA_E_QUEDA_DE_AERONAVES_PREDIO: 2500000,
    DANOS_ELETRICOS_VERBA_UNICA: 62500,
    ROUBO_E_OU_SUBTRACAO_DE_BENS_COM_VESTIGIOS_VERBA_UNICA: 50000,
    VENDAVAL_FURACAO_CICLONE_TORNADO_E_GRANIZO_VERBA_UNICA: 93750,
    PERDA_OU_PAGAMENTO_DE_ALUGUEL_VERBA_UNICA: 200000,
    RESPONSABILIDADE_CIVIL_FAMILIAR_VERBA_UNICA: 100000,
    QUEBRA_DE_VIDROS_ESPELHOS_MARMORES_E_GRANITOS_VERBA_UNICA: 150000,
    ROMPIMENTO_DE_TUBULACOES_DE_AGUA_VERBA_UNICA: 50000
  },
  'Comércio e Serviço': {
    INCENDIO_EXPLOSAO_DE_QUALQUER_NATUREZA_FUMACA_E_QUEDA_DE_AERONAVES_PREDIO_E_CONTEUDO:
      1000000,
    INCENDIO_EXPLOSAO_DE_QUALQUER_NATUREZA_FUMACA_E_QUEDA_DE_AERONAVES_CONTEUDO: 207000,
    INCENDIO_EXPLOSAO_DE_QUALQUER_NATUREZA_FUMACA_E_QUEDA_DE_AERONAVES_PREDIO: 1000000,
    DANOS_ELETRICOS_VERBA_UNICA: 62500,
    ROUBO_E_OU_SUBTRACAO_DE_BENS_COM_VESTIGIOS_VERBA_UNICA: 50000,
    VENDAVAL_FURACAO_CICLONE_TORNADO_E_GRANIZO_VERBA_UNICA: 93750,
    PERDA_OU_PAGAMENTO_DE_ALUGUEL_VERBA_UNICA: 80000,
    RESPONSABILIDADE_CIVIL_FAMILIAR_VERBA_UNICA: 100000,
    QUEBRA_DE_VIDROS_ESPELHOS_MARMORES_E_GRANITOS_VERBA_UNICA: 150000,
    ROMPIMENTO_DE_TUBULACOES_DE_AGUA_VERBA_UNICA: 10000
  }
};

const PwaYelumBudgetForm: React.FC<YelumBudgetFormProps> = ({
  estate,
  insurerId,
  percentage,
  onBudgetCreated,
  onBack
}) => {
  const [step, setStep] = useState<AnalysisStep>('LOADING');
  const [loadingMsg, setLoadingMsg] = useState('Preparando sua cotação Yelum...');
  const [resultData, setResultData] = useState<any>(null);
  const [allCoverages, setAllCoverages] = useState<any[]>([]);
  const [fireCoverages, setFireCoverages] = useState<any[]>([]);
  const [assistanceCoverages, setAssistanceCoverages] = useState<any[]>([]);
  const [selectedAssistance, setSelectedAssistance] = useState<string | null>(null);
  const [hasYelumPolicies, setHasYelumPolicies] = useState<boolean>(false);
  const [configs, setConfigs] = useState<any>({});

  const [initialValues, setInitialValues] = useState<any>({
    propertyType: 'Apartamento', // Default
    coverageType: 'Prédio e Conteúdo',
    startVigency: moment().format('YYYY-MM-DD'),
    endVigency: moment().add(1, 'year').format('YYYY-MM-DD'),
    installments: 12,
    rentValue: 0,
    fireInsuranceValue: 0,
    coverages: [],
    brokerCommission: percentage || 10,
    commissionTax: 0, // Estate compensation
  });

  const propertyTypeOptions = [
    { key: 7, text: 'Apartamento', value: 'Apartamento' },
    { key: 8, text: 'Residência Alvenaria', value: 'Residência Alvenaria' },
    { key: 1, text: 'Escritório e Consultório', value: 'Escritório e Consultório' },
    { key: 3, text: 'Comércio e Serviço', value: 'Comércio e Serviço' },
    { key: 9, text: 'Casa Madeira', value: 'Casa Madeira' }
  ];

  const coverageTypeOptions = [{ key: 3, text: 'Prédio e Conteúdo', value: 'Prédio e Conteúdo' }];

  const allInstallmentsOptions = [
    { key: 1, text: '1x', value: 1 },
    { key: 2, text: '2x', value: 2 },
    { key: 3, text: '3x', value: 3 },
    { key: 4, text: '4x', value: 4 },
    { key: 5, text: '5x', value: 5 },
    { key: 6, text: '6x', value: 6 },
    { key: 7, text: '7x', value: 7 },
    { key: 8, text: '8x', value: 8 },
    { key: 9, text: '9x', value: 9 },
    { key: 10, text: '10x', value: 10 },
    { key: 11, text: '11x', value: 11 },
    { key: 12, text: '12x', value: 12 }
  ];
  const fixedInstallmentsOptions = [{ key: 12, text: '12x', value: 12 }];
  const installmentsOptions = configs?.flexibleInstallmentsEnabled
    ? allInstallmentsOptions
    : fixedInstallmentsOptions;

  const getCoverageLabelFromCoverage = (coverage: any): string | null => {
    if (!coverage || !coverage.label) {
      return null;
    }
    const label = coverage.label;
    return label.replace(/_\d+$/, '');
  };

  const getLimitByCoverageLike = (
    coverageLabel: string,
    propertyLimits: Record<string, number>
  ): number | undefined => {
    if (coverageLabel.startsWith('INCENDIO')) {
      const matchingKey = Object.keys(propertyLimits).find(key => key.startsWith('INCENDIO'));
      return matchingKey ? propertyLimits[matchingKey] : undefined;
    }
    const words = coverageLabel.split('_');
    if (words.length >= 2) {
      const searchPattern = `${words[0]}_${words[1]}`;
      const matchingKey = Object.keys(propertyLimits).find(key => key.startsWith(searchPattern));
      return matchingKey ? propertyLimits[matchingKey] : undefined;
    }
    return propertyLimits[coverageLabel];
  };

  const getFireCoverageByType = (coverageType: string, propertyType?: string) => {
    const codeMap: { [key: string]: string } = {
      'Prédio e Conteúdo': 'E10',
      Prédio: 'E11',
      Conteúdo: 'E12'
    };
    const codeSuffix = codeMap[coverageType];
    return fireCoverages.find(fc => {
      if (!fc.coverage?.code) return false;
      const codeParts = fc.coverage.code.split('-');
      const codeMatches = codeParts.length > 1 && codeParts[1] === codeSuffix;
      if (propertyType && fc.propertyType) return codeMatches && fc.propertyType === propertyType;
      return codeMatches;
    });
  };

  const createCoveragesWithDefaults = useCallback(
    (coveragesList: any[], rentValue = 0): CoverageControl[] => {
      return coveragesList.map(c => {
        let value = 0;
        const defaultValue = c.defaultValue ?? c.coverage?.defaultValue;
        const defaultMultiplier = c.defaultMultiplier ?? c.multiplier ?? c.coverage?.defaultMultiplier;
        const defaultEnabled = c.defaultEnabled ?? c.coverage?.defaultEnabled ?? c.isRequired;

        if (defaultValue && defaultValue > 0) value = defaultValue;
        else if (defaultMultiplier && defaultMultiplier > 0 && rentValue > 0) value = defaultMultiplier * rentValue;
        else if (c.multiplier && c.multiplier > 0 && rentValue > 0) value = c.multiplier * rentValue;

        if (c.maxValue && value > c.maxValue) value = c.maxValue;
        if (c.minValue && value < c.minValue) value = c.minValue;

        return {
          ...c,
          id: c.coverage?.id,
          code: c.coverage?.code,
          name: c.coverage?.name,
          checked: defaultEnabled || false,
          onEdit: false,
          value,
          onEditValue: value
        };
      });
    },
    []
  );

  const applyDefaultCoverageValues = (
    coveragesList: CoverageControl[],
    rentValue: number
  ): CoverageControl[] => {
    return coveragesList.map(c => {
      let value = 0;
      const defaultValue = c.defaultValue ?? c.coverage?.defaultValue;
      const defaultMultiplier = c.defaultMultiplier ?? c.multiplier ?? c.coverage?.defaultMultiplier;
      const defaultEnabled = c.defaultEnabled ?? c.coverage?.defaultEnabled ?? c.isRequired;

      if (defaultValue && defaultValue > 0) value = defaultValue;
      else if (defaultMultiplier && defaultMultiplier > 0) value = defaultMultiplier * rentValue;
      else if (c.multiplier && c.multiplier > 0) value = c.multiplier * rentValue;

      if (c.maxValue && value > c.maxValue) value = c.maxValue;
      if (c.minValue && value < c.minValue) value = c.minValue;

      return {
        ...c,
        value,
        onEditValue: value,
        checked: defaultEnabled || c.checked
      };
    });
  };

  const filterCoveragesByPropertyType = useCallback(
    (propertyType: string | null, rentValue = 0) => {
      if (!propertyType || allCoverages.length === 0) return [];

      const assistances = allCoverages.filter(c => {
        if (!c.coverage?.label?.toUpperCase().includes('ASSISTENCIA') || c.propertyType) return false;
        const label = c.coverage.label;
        if (['Apartamento', 'Residência Alvenaria', 'Casa Madeira'].includes(propertyType)) {
          return label.includes('11054');
        }
        if (['Escritório e Consultório', 'Comércio e Serviço'].includes(propertyType)) {
          return label.includes('11112');
        }
        return false;
      });

      const fires = allCoverages.filter(
        c =>
          c.coverage?.label?.toUpperCase().includes('INCENDIO') &&
          ['E10', 'E11', 'E12'].includes(c.coverage?.code?.split('-')[1]) &&
          c.propertyType === propertyType
      );

      const otherCoverages = allCoverages.filter(
        c =>
          !c.coverage?.label?.toUpperCase().includes('ASSISTENCIA') &&
          !(
            c.coverage?.label?.toUpperCase().includes('INCENDIO') &&
            ['E10', 'E11', 'E12'].includes(c.coverage?.code?.split('-')[1])
          ) &&
          c.propertyType === propertyType
      );

      const uniqueAssistances = assistances.reduce((acc, current) => {
        const exists = acc.find((item: any) => item.coverage.id === current.coverage.id);
        if (!exists) acc.push(current);
        return acc;
      }, [] as any[]);

      setAssistanceCoverages(uniqueAssistances);
      setFireCoverages(fires);
      return createCoveragesWithDefaults(otherCoverages, rentValue);
    },
    [allCoverages, createCoveragesWithDefaults]
  );

  const coveragesValidation = (
    coverages: CoverageControl[],
    propertyType: string,
    fireInsuranceValue: number,
    coverageType: string
  ) => {
    if (!propertyType) {
      warningNotification(
        'Tipo de imóvel não selecionado',
        'Por favor, selecione o tipo de imóvel antes de criar o orçamento.'
      );
      return false;
    }
    if (!fireInsuranceValue || fireInsuranceValue <= 0) {
      warningNotification(
        'Cobertura de Incêndio obrigatória',
        'Por favor, informe o valor da cobertura de incêndio.'
      );
      return false;
    }

    const propertyLimits = YELUM_PROPERTY_LIMITS[propertyType];
    if (propertyLimits) {
      const fireCoverage = getFireCoverageByType(coverageType, propertyType);
      if (fireCoverage && fireCoverage.coverage) {
        const coverageLabel = getCoverageLabelFromCoverage(fireCoverage.coverage);
        if (coverageLabel) {
          const fireLimit = getLimitByCoverageLike(coverageLabel, propertyLimits);
          if (fireLimit !== undefined && fireInsuranceValue > fireLimit) {
            warningNotification(
              'Valor da cobertura de incêndio excede o limite',
              `O valor da cobertura de incêndio excede o limite máximo de ${numberToMoneyString(fireLimit)} para este tipo de imóvel.`
            );
            return false;
          }
        }
      }
    }

    const checkedCoverages = (coverages || []).filter(c => c.checked);
    const invalidCoverages = checkedCoverages.filter(c => !c.value || c.value <= 0);
    if (invalidCoverages.length > 0) {
      warningNotification(
        'Valores de cobertura inválidos',
        'Todas as coberturas selecionadas devem ter valores maiores que zero.'
      );
      return false;
    }

    if (propertyLimits) {
      for (const coverage of checkedCoverages) {
        const coverageLabel = getCoverageLabelFromCoverage(coverage.coverage);
        if (!coverageLabel) continue;
        const limit = getLimitByCoverageLike(coverageLabel, propertyLimits);
        if (limit !== undefined && coverage.value > limit) {
          warningNotification(
            'Valor da cobertura excede o limite',
            `A cobertura "${coverage.name}" excede o limite máximo de ${numberToMoneyString(limit)}.`
          );
          return false;
        }
      }
    }
    return true;
  };

  const validateOptionsAndLoadCoverages = useCallback(async () => {
    try {
      const cvgs = await get(
        `/alfa-seguros/coverage/coverage-estate/${estate.id}?insurerId=${insurerId}`
      );
      const loadedConfigs = await get(`/estates/fire-active-insurer/${estate.id}/${insurerId}`);
      const hasPoliciesRes = await get(`/fire-insurance/yelum/estate/${estate.id}/has-policies`);
      setHasYelumPolicies(!!hasPoliciesRes?.hasPolicies);
      setConfigs(loadedConfigs || {});
      setAllCoverages(cvgs || []);

      const filteredCoverages = filterCoveragesByPropertyType('Apartamento', 0);
      const fireCoverage = (cvgs || []).find(
        (c: any) =>
          c.coverage?.label?.toUpperCase().includes('INCENDIO') &&
          ['E10', 'E11', 'E12'].includes(c.coverage?.code?.split('-')[1]) &&
          c.propertyType === 'Apartamento' &&
          c.coverage?.code?.split('-')[1] === 'E10'
      );
      const fireValue = fireCoverage?.defaultValue || 0;
      setInitialValues((prev: any) => ({
        ...prev,
        commissionTax: loadedConfigs?.compensation || 0,
        brokerCommission: loadedConfigs?.brokerCommission || prev.brokerCommission || 0,
        fireInsuranceValue: fireValue,
        coverages: filteredCoverages
      }));
      setStep('FORM');
    } catch (error) {
      console.error(error);
      setStep('FORM');
    }
  }, [estate.id, insurerId, filterCoveragesByPropertyType]);

  useEffect(() => {
    validateOptionsAndLoadCoverages();
  }, [validateOptionsAndLoadCoverages]);

  const validationSchema = Yup.object().shape({
    propertyType: Yup.string().required('Obrigatório'),
    coverageType: Yup.string().required('Obrigatório'),
    startVigency: Yup.string().required('Obrigatório'),
    endVigency: Yup.string().required('Obrigatório'),
    rentValue: Yup.number().moreThan(0, 'Obrigatório'),
    fireInsuranceValue: Yup.number().moreThan(0, 'Obrigatório')
  });

  const handleSubmit = async (values: any, { setSubmitting }: any) => {
    setLoadingMsg('Enviando cotação para a Seguradora...');
    setStep('LOADING');

    try {
      const calculationPayload = {
        estateId: estate.id,
        insurerId: insurerId,
        data: {
          rentValue: Number(values.rentValue),
          policyPeriodStart: values.startVigency,
          policyPeriodEnd: values.endVigency,
          propertyType: values.propertyType,
          coverageType: values.coverageType,
          commissionTax: Number(values.commissionTax),
          brokerCommission: Number(values.brokerCommission),
          assistanceType:
            selectedAssistance && selectedAssistance !== '-'
              ? assistanceCoverages
                .find((a: any) => String(a.coverage.id) === String(selectedAssistance))
                ?.coverage?.name?.toLowerCase()
                ?.includes('completo')
                ? 'Completa'
                : 'Básica'
              : 'Sem Assistência',
          coverages: (() => {
            const selectedCoverages = values.coverages
              .filter((c: CoverageControl) => c.checked)
              .map((c: CoverageControl) => ({ id: c.id, value: c.value }));

            const fireCoverage = getFireCoverageByType(values.coverageType, values.propertyType);
            if (fireCoverage && values.fireInsuranceValue) {
              selectedCoverages.unshift({
                id: fireCoverage.coverage.id,
                value: values.fireInsuranceValue
              });
            }

            if (selectedAssistance && selectedAssistance !== '-') {
              const assistance = assistanceCoverages.find(
                (a: any) => String(a.coverage.id) === String(selectedAssistance)
              );
              if (assistance) {
                selectedCoverages.push({ id: assistance.coverage.id, value: 0 });
              }
            }
            return selectedCoverages;
          })()
        }
      };

      // 1. Calculate / Quote
      const quoteRes: any = await post('/fire-insurance-quotations/yelum-seguros/quote', calculationPayload);

      if (!quoteRes || !quoteRes.totalAnnual) {
        throw new Error('Cotação falhou');
      }

      // 2. Budget (Assume standard route maps to save)
      const budgetPayload = {
        estateId: estate.id,
        insurerId,
        isIframe: false,
        propertyType: values.propertyType,
        payload: {
          quotationData: quoteRes,
          valorAluguel: values.rentValue,
          policyPeriodStart: values.startVigency,
          policyPeriodEnd: values.endVigency,
          coverageType: values.coverageType,
        }
      };

      // Save via generic Yelum budget endpoint
      const res: any = await post('/fire-insurance-quotations/yelum-seguros/budget', budgetPayload);

      setResultData({
        maxPremium: quoteRes.totalAnnual,
        fireInsuranceId: res?.fireInsuranceId || res?.id || undefined,
        ...quoteRes
      });

      if (onBudgetCreated && (res?.fireInsuranceId || res?.id)) {
        onBudgetCreated(res?.fireInsuranceId || res?.id);
      }

      successNotification('Sucesso', 'Orçamento Yelum criado com sucesso!');
      setStep('RESULT');

    } catch (e) {
      console.error(e);
      warningNotification('Ops', 'Ocorreu um erro ao realizar a cotação Yelum.');
      setStep('FORM');
    } finally {
      setSubmitting(false);
    }
  };


  if (step === 'LOADING') {
    return (
      <Container>
        <Header>
          <HeaderLogo src={logorv} alt="Logo" />
        </Header>
        <LoadingContainer>
          <LoadingText>{loadingMsg}</LoadingText>
          <ProgressTrack>
            <ProgressBar />
          </ProgressTrack>
        </LoadingContainer>
      </Container>
    );
  }

  if (step === 'RESULT' && resultData) {
    return (
      <Container>
        <Header>
          <button style={{ background: 'none', border: 'none', color: '#59249b', display: 'flex', alignItems: 'center', gap: 4 }} onClick={onBack}>
            <FaChevronLeft /> Voltar
          </button>
          <HeaderLogo src={logorv} alt="Logo" />
        </Header>
        <Content>
          <SubHeader>
            <AnalysisTitle>Resultado da Cotação</AnalysisTitle>
          </SubHeader>
          <ResultCard>
            <CardTopRow>
              <InsurerLogo color="#FFBF00">YLM</InsurerLogo>
              <InsurerInfo>
                <InsurerName>Yelum Seguros</InsurerName>
                <StatusBadge type="approved">
                  <StatusDot type="approved" />
                  <StatusText>Pronto para Contratação</StatusText>
                </StatusBadge>
              </InsurerInfo>
            </CardTopRow>
            <Divider />
            <DetailsRow>
              <DetailColumn>
                <DetailLabel>Prêmio Total</DetailLabel>
                <DetailValue>{numberToMoneyString(resultData.maxPremium)}</DetailValue>
              </DetailColumn>
              <DetailColumn style={{ alignItems: 'flex-end' }}>
                <DetailLabel>ID do Orçamento</DetailLabel>
                <DetailValue>{resultData.fireInsuranceId?.split('-')[0] || 'N/A'}</DetailValue>
              </DetailColumn>
            </DetailsRow>
          </ResultCard>

          <ActionButtonsRow style={{ marginTop: 'auto' }}>
            <FinalBackButton onClick={onBack}>Novo Orçamento / Voltar</FinalBackButton>
          </ActionButtonsRow>
        </Content>
      </Container>
    );
  }

  return (
    <Container>
      <Header>
        <button style={{ background: 'none', border: 'none', color: '#59249b', display: 'flex', alignItems: 'center', gap: 4 }} onClick={onBack}>
          <FaChevronLeft /> Voltar
        </button>
        <HeaderLogo src={logorv} alt="Logo" />
      </Header>

      <Content>
        <ValidationContainer>
          <ValidateTitle>
            <span>Yelum Seguradora</span>
          </ValidateTitle>

          <Formik
            enableReinitialize
            initialValues={initialValues}
            validationSchema={validationSchema}
            onSubmit={handleSubmit}
          >
            {({ values, setFieldValue, submitForm, isSubmitting }) => (
              <FormSection>
                <ValidationSectionTitle>Dados do Seguro</ValidationSectionTitle>

                <InputGroup>
                  <FieldLabel>Tipo do Imóvel</FieldLabel>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
                    {propertyTypeOptions.map(opt => (
                      <RadioButton
                        key={opt.key}
                        active={values.propertyType === opt.value}
                        onClick={() => {
                          setFieldValue('propertyType', opt.value);
                          setSelectedAssistance(null);
                          const filteredCoverages = filterCoveragesByPropertyType(
                            opt.value,
                            Number(values.rentValue || 0)
                          );
                          setFieldValue('coverages', filteredCoverages);
                          const fireCoverage = getFireCoverageByType(values.coverageType, opt.value);
                          if (fireCoverage && Number(values.rentValue || 0) > 0) {
                            let fireValue = fireCoverage.multiplier * Number(values.rentValue || 0);
                            if (fireCoverage.maxValue && fireValue > fireCoverage.maxValue) {
                              fireValue = fireCoverage.maxValue;
                            }
                            if (fireCoverage.minValue && fireValue < fireCoverage.minValue) {
                              fireValue = fireCoverage.minValue;
                            }
                            setFieldValue('fireInsuranceValue', fireValue);
                          } else {
                            setFieldValue('fireInsuranceValue', 0);
                          }
                        }}
                        style={{ height: 'auto', minHeight: '48px', padding: '8px' }}
                      >
                         <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <CustomRadio active={values.propertyType === opt.value} />
                          <span style={{ fontSize: '13px' }}>{opt.text}</span>
                        </div>
                      </RadioButton>
                    ))}
                  </div>
                </InputGroup>

                <InputGroup>
                  <FieldLabel>Tipo de Cobertura</FieldLabel>
                  <RadioGroup>
                    {coverageTypeOptions.map(opt => (
                      <RadioButton
                        key={opt.key}
                        active={values.coverageType === opt.value}
                        style={{ opacity: 0.6, cursor: 'not-allowed' }}
                      >
                         <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <CustomRadio active={values.coverageType === opt.value} />
                          <span>{opt.text}</span>
                        </div>
                      </RadioButton>
                    ))}
                  </RadioGroup>
                </InputGroup>

                <div
                  style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
                    gap: '12px',
                    width: '100%'
                  }}
                >
                  <InputGroup style={{ width: '100%', minWidth: 0 }}>
                    <Input
                      name="rentValue"
                      label="Valor do Aluguel"
                      placeholder="R$ 0,00"
                      value={values.rentValue ? numberToMoneyString(values.rentValue) : ''}
                      onChange={(e: any) => {
                        const onlyNumber = e.target.value.replace(/\D/g, '');
                        const value = Number(onlyNumber) / 100;
                        setFieldValue('rentValue', value);
                      }}
                      onBlur={() => {
                        if (!values.rentValue || Number(values.rentValue) <= 0) return;
                        const updatedCoverages = applyDefaultCoverageValues(
                          values.coverages || [],
                          Number(values.rentValue)
                        );
                        setFieldValue('coverages', updatedCoverages);
                        const fireCoverage = getFireCoverageByType(
                          values.coverageType,
                          values.propertyType
                        );
                        if (fireCoverage && fireCoverage.multiplier) {
                          let fireValue = fireCoverage.multiplier * Number(values.rentValue);
                          if (fireCoverage.maxValue && fireValue > fireCoverage.maxValue) {
                            fireValue = fireCoverage.maxValue;
                          }
                          if (fireCoverage.minValue && fireValue < fireCoverage.minValue) {
                            fireValue = fireCoverage.minValue;
                          }
                          setFieldValue('fireInsuranceValue', fireValue);
                        }
                      }}
                    />
                  </InputGroup>
                  <InputGroup style={{ width: '100%', minWidth: 0 }}>
                    <FieldLabel>Parcelas</FieldLabel>
                    <Dropdown
                      fluid
                      selection
                      options={installmentsOptions}
                      value={values.installments}
                      onChange={(_, { value }) => setFieldValue('installments', value)}
                      disabled={!configs?.flexibleInstallmentsEnabled}
                    />
                  </InputGroup>
                </div>

                <InputGroup>
                  <FieldLabel>Período de Vigência</FieldLabel>
                  <div style={{ 
                    background: '#F9FAFB', 
                    border: '1px solid #E5E7EB', 
                    borderRadius: '8px', 
                    padding: '16px',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '12px'
                  }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <span style={{ fontSize: '14px', color: '#6B7280', fontWeight: 600 }}>INÍCIO</span>
                      <StyledInput
                        type="date"
                        value={values.startVigency}
                        onChange={(e) => {
                          if (!hasYelumPolicies) {
                            const today = moment().startOf('day');
                            const selected = moment(e.target.value).startOf('day');
                            if (selected.isBefore(today)) {
                              warningNotification(
                                'Data inválida',
                                'Imobiliárias novas não podem selecionar data retroativa.'
                              );
                              return;
                            }
                          }
                          setFieldValue('startVigency', e.target.value);
                          setFieldValue('endVigency', moment(e.target.value).add(1, 'year').format('YYYY-MM-DD'));
                        }}
                        style={{ width: 'auto', textAlign: 'right', fontWeight: 700, color: '#59249B' }}
                      />
                    </div>
                    <div style={{ height: '1px', background: '#E5E7EB' }} />
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <span style={{ fontSize: '14px', color: '#6B7280', fontWeight: 600 }}>TÉRMINO</span>
                      <span style={{ fontWeight: 700, color: '#393939' }}>
                        {moment(values.endVigency).format('DD/MM/YYYY')}
                      </span>
                    </div>
                  </div>
                </InputGroup>

                <InputGroup>
                  <FieldLabel>Assistência</FieldLabel>
                  <div
                    style={{
                      display: 'flex',
                      flexDirection: 'column',
                      gap: '10px',
                      width: '100%'
                    }}
                  >
                    <RadioButton
                      active={!selectedAssistance || selectedAssistance === '-'}
                      onClick={() => setSelectedAssistance('-')}
                      style={{
                        justifyContent: 'flex-start',
                        width: '100%',
                        minHeight: '56px',
                        padding: '12px 14px'
                      }}
                    >
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <CustomRadio active={!selectedAssistance || selectedAssistance === '-'} />
                        <span style={{ fontSize: '15px', lineHeight: 1.25 }}>Sem assistência</span>
                      </div>
                    </RadioButton>
                    {assistanceCoverages.map((ac: any, idx: number) => {
                      const assistanceValue = String(ac.coverage?.id);
                      return (
                        <RadioButton
                          key={ac.coverage?.id || idx}
                          active={selectedAssistance === assistanceValue}
                          onClick={() => setSelectedAssistance(assistanceValue)}
                          style={{
                            justifyContent: 'flex-start',
                            width: '100%',
                            minHeight: '56px',
                            padding: '12px 14px'
                          }}
                        >
                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <CustomRadio active={selectedAssistance === assistanceValue} />
                            <span style={{ fontSize: '15px', lineHeight: 1.25 }}>
                              {ac.coverage?.name?.trim()}
                            </span>
                          </div>
                        </RadioButton>
                      );
                    })}
                  </div>
                </InputGroup>

                <ValidationSectionTitle style={{ marginTop: 16, marginBottom: 8 }}>
                  Coberturas Disponíveis
                </ValidationSectionTitle>

                <div
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '8px',
                    marginBottom: '8px'
                  }}
                >
                  <div
                    style={{
                      display: 'flex',
                      flexDirection: 'column',
                      padding: '12px 16px',
                      background: '#F9FAFB',
                      borderRadius: '8px',
                      border: '1px solid #E5E7EB'
                    }}
                  >
                    <div
                      style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        gap: 12
                      }}
                    >
                      <span style={{ fontSize: 13, fontWeight: 700, color: '#111827' }}>
                        INCÊNDIO / EXPLOSÃO / FUMAÇA / Q. AERONAVES
                        <span
                          style={{
                            color: '#EF4444',
                            fontSize: 10,
                            display: 'block',
                            marginTop: 2
                          }}
                        >
                          OBRIGATÓRIA
                        </span>
                      </span>
                      <input
                        type="checkbox"
                        checked={true}
                        disabled
                        style={{ width: 18, height: 18, accentColor: '#59249B' }}
                      />
                    </div>
                    <div style={{ marginTop: 8 }}>
                      <StyledInput
                        placeholder="R$ 0,00"
                        value={
                          values.fireInsuranceValue
                            ? numberToMoneyString(+values.fireInsuranceValue)
                            : ''
                        }
                        onChange={(e: any) => {
                          const onlyNumber = e.target.value.replace(/\D/g, '');
                          const value = Number(onlyNumber) / 100;
                          setFieldValue('fireInsuranceValue', value);
                        }}
                        style={{ width: '100%', textAlign: 'right' }}
                      />
                    </div>
                  </div>

                  {(values.coverages || []).map((cov: CoverageControl, index: number) => (
                    <div
                      key={cov.code || index}
                      style={{
                        display: 'flex',
                        flexDirection: 'column',
                        padding: '12px 16px',
                        background: '#F9FAFB',
                        borderRadius: '8px',
                        border: '1px solid #E5E7EB'
                      }}
                    >
                      <div
                        style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center'
                        }}
                      >
                        <div
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 10,
                            flex: 1,
                            paddingRight: 8
                          }}
                        >
                          <input
                            type="checkbox"
                            checked={cov.checked}
                            disabled={cov.isRequired}
                            style={{
                              width: 18,
                              height: 18,
                              accentColor: '#59249B',
                              cursor: cov.isRequired ? 'not-allowed' : 'pointer'
                            }}
                            onChange={() => {
                              if (cov.isRequired) return;
                              setFieldValue(`coverages[${index}].checked`, !cov.checked);
                            }}
                          />
                          <span
                            style={{
                              fontSize: 14,
                              color: cov.checked ? '#111827' : '#9CA3AF',
                              fontWeight: cov.checked ? 600 : 400,
                              lineHeight: 1.2
                            }}
                          >
                            {cov.name}
                            {cov.isRequired && (
                              <span
                                style={{
                                  color: '#EF4444',
                                  fontSize: 10,
                                  display: 'block',
                                  marginTop: 2,
                                  fontWeight: 700
                                }}
                              >
                                OBRIGATÓRIO
                              </span>
                            )}
                          </span>
                        </div>
                        {cov.checked && (
                          <div style={{ minWidth: 100, textAlign: 'right' }}>
                            {!cov.onEdit ? (
                              <strong
                                style={{
                                  fontSize: 14,
                                  color: '#111827',
                                  whiteSpace: 'nowrap',
                                  cursor: 'pointer'
                                }}
                                onClick={() => {
                                  setFieldValue(`coverages[${index}].onEdit`, true);
                                  setFieldValue(
                                    `coverages[${index}].onEditValue`,
                                    cov.value || cov.minValue || 0
                                  );
                                }}
                              >
                                {numberToMoneyString(cov.value || 0)}
                              </strong>
                            ) : (
                              <StyledInput
                                placeholder="R$ 0,00"
                                style={{
                                  width: 100,
                                  padding: 6,
                                  fontSize: 13,
                                  textAlign: 'right',
                                  border: '1px solid #D1D5DB',
                                  borderRadius: 4,
                                  margin: 0
                                }}
                                value={
                                  cov.onEditValue ? numberToMoneyString(cov.onEditValue) : ''
                                }
                                onChange={(e: any) => {
                                  const onlyNumber = e.target.value.replace(/\D/g, '');
                                  const val = Number(onlyNumber) / 100;
                                  setFieldValue(`coverages[${index}].onEditValue`, val);
                                }}
                                onBlur={() => {
                                  const val = cov.onEditValue;
                                  if (cov.minValue && val < cov.minValue) {
                                    warningNotification(
                                      'Valor inválido',
                                      `O valor para ${cov.name} deve ser maior que ${numberToMoneyString(cov.minValue)}.`
                                    );
                                    return;
                                  }
                                  if (cov.maxValue && val > cov.maxValue) {
                                    warningNotification(
                                      'Valor inválido',
                                      `O valor para ${cov.name} deve ser menor que ${numberToMoneyString(cov.maxValue)}.`
                                    );
                                    return;
                                  }
                                  setFieldValue(`coverages[${index}].value`, val);
                                  setFieldValue(`coverages[${index}].onEdit`, false);
                                }}
                              />
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>

                <FooterButtons>
                  <BackButton onClick={onBack} disabled={isSubmitting}>Cancelar</BackButton>
                  <SubmitButton
                    type="button"
                    onClick={() => {
                      if (
                        coveragesValidation(
                          values.coverages || [],
                          values.propertyType,
                          values.fireInsuranceValue,
                          values.coverageType
                        )
                      ) {
                        submitForm();
                      }
                    }}
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? 'Cotando...' : 'Cotar Agora'}
                  </SubmitButton>
                </FooterButtons>
              </FormSection>
            )}
          </Formik>
        </ValidationContainer>
      </Content>
    </Container>
  );
};

export default PwaYelumBudgetForm;
